import React from 'react';
import { relicsPool } from '../data/relics';
import { pickRelicsByRarity } from '../relics/relicRarity';
import { isRansackedChestExclusiveRelic } from '../map/ransackedChest';
import { makeRng } from '../../utils/makeRng';

function isStoryExclusiveAllyCard(card) {
  const subtitle = String(card?.subtitle ?? '').trim().toLowerCase();
  const specialType = String(card?.specialType ?? '').trim().toLowerCase();
  if (specialType === 'ally') return true;
  return /(^|\b)ally\s+(attack|defense|utility)(\b|$)/i.test(subtitle);
}

export function useMerchantStock({
  initialMerchantStock = null,
  currentFloorMood,
  floorLevel,
  tarotFreeTrinkets,
  tarotShopRarity,
  tarotNoMerchant,
  combatLibrary,
  ownedRelics,
  playerDeckIds,
    runSeedRef,
  currentFloorSize,
  getSpawnForSize,

  teeth,
  setTeeth,
  maxTeeth,

  keys,
  setKeys,
  maxKeys,

  torches,
  setTorches,
  maxTorches,

  potions,
  setPotions,
  maxPotions,

  setSanity,
  setOwnedRelics,
  setPlayerDeckIds,
  setNextCombatCost0,

  TRINKET_LIMIT,
}) {
  const [isMerchantOpen, setIsMerchantOpen] = React.useState(false);
  const [merchantStock, setMerchantStock] = React.useState(() => initialMerchantStock);
  const [activeMerchantKey, setActiveMerchantKey] = React.useState(null);

// Merchants should always roll stock per map position so multiple shops on the
// same floor do not mirror each other outside of Populated floors.
const perMerchantStockEnabled =
  (currentFloorMood?.merchantStockMode ?? 'per-merchant') === 'per-merchant';


  const getMerchantPrices = React.useCallback(() => {
    const base = Math.min(4, 2 + Math.floor((floorLevel - 1) / 2));
    const cardPrice = base;
    const trinketBase = Math.min(6, base + 2);

    return {
      cardPrice,
      trinketPrice: tarotFreeTrinkets ? 0 : trinketBase,
    };
  }, [floorLevel, tarotFreeTrinkets]);

  const trinketWeights = React.useMemo(() => {
    if (tarotShopRarity === 'more') return { common: 0.3, uncommon: 0.3, rare: 0.4 };
    if (tarotShopRarity === 'less') return { common: 0.8, uncommon: 0.15, rare: 0.05 };
    return { common: 0.6, uncommon: 0.3, rare: 0.1 };
  }, [tarotShopRarity]);

  const buildOneMerchantStock = React.useCallback((merchantKey = null) => {
  if (tarotNoMerchant) return null;

  const { cardPrice, trinketPrice } = getMerchantPrices();

  const lib = Array.isArray(combatLibrary) ? combatLibrary : [];
  const eligibleCardsAll = lib.filter((c) => c && c.id && c.kind !== 'enemy' && !c.acquisitionLocked && !isStoryExclusiveAllyCard(c));
  const ownedRelicIds = new Set((ownedRelics || []).map((r) => r.id));
  const deckSet = new Set(playerDeckIds || []);

  const spawn = getSpawnForSize(currentFloorSize);

  let seed =
    ((runSeedRef.current ?? 0) ^
      (floorLevel * 0x9e3779b9) ^
      (spawn.x << 16) ^
      spawn.y) >>> 0;

  if (perMerchantStockEnabled && merchantKey) {
    const [mxRaw, myRaw] = String(merchantKey).split(',');
    const mx = Math.floor(Number(mxRaw) || 0);
    const my = Math.floor(Number(myRaw) || 0);

    seed =
      (seed ^
        Math.imul(mx + 1, 0x85ebca6b) ^
        Math.imul(my + 1, 0xc2b2ae35)) >>> 0;
  }

  const rand = makeRng(seed);

  const pickUnique = (arr, count, allowFn) => {
    const pool = (arr || []).filter((x) => (allowFn ? allowFn(x) : true));

    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    const picked = [];
    const used = new Set();

    for (const it of pool) {
      if (!it) continue;
      if (used.has(it.id)) continue;
      used.add(it.id);
      picked.push(it);
      if (picked.length >= count) break;
    }

    return picked;
  };

  let cardPicks = pickUnique(eligibleCardsAll, 4, (c) => !deckSet.has(c.id));

  if (cardPicks.length < 4) {
    const more = pickUnique(eligibleCardsAll, 4 - cardPicks.length, () => true);
    const have = new Set(cardPicks.map((c) => c.id));

    for (const c of more) {
      if (!have.has(c.id)) cardPicks.push(c);
      if (cardPicks.length >= 4) break;
    }
  }

  const trinketEligible = relicsPool.filter(
    (r) => r && r.id && !ownedRelicIds.has(r.id) && !isRansackedChestExclusiveRelic(r)
  );

  const trinketPicks = pickRelicsByRarity(trinketEligible, 2, rand, trinketWeights);

  return {
    cardPrice,
    trinketPrice,
    cards: cardPicks.map((c) => ({
      id: c.id,
      title: c.title,
      subtitle: c.subtitle,
      tag: c.tag,
      kind: c.kind,
      description: c.description,
      img: c.img,
    })),
    trinkets: trinketPicks,
  };
}, [
  combatLibrary,
  currentFloorSize,
  floorLevel,
  getMerchantPrices,
  getSpawnForSize,
  ownedRelics,
  perMerchantStockEnabled,
  playerDeckIds,
  runSeedRef,
  tarotNoMerchant,
  trinketWeights,
]);

const buildMerchantStock = React.useCallback((merchantKey = activeMerchantKey) => {
  if (tarotNoMerchant) {
    setMerchantStock(null);
    return;
  }

  const nextStock = buildOneMerchantStock(merchantKey);
  if (!nextStock) {
    setMerchantStock(null);
    return;
  }

  if (perMerchantStockEnabled && merchantKey) {
    setMerchantStock((prev) => ({
      floor: floorLevel,
      mode: 'per-merchant',
      byMerchantKey: {
        ...(
          prev?.floor === floorLevel &&
          prev?.mode === 'per-merchant' &&
          prev?.byMerchantKey
            ? prev.byMerchantKey
            : {}
        ),
        [merchantKey]: nextStock,
      },
    }));
    return;
  }

  setMerchantStock({
    floor: floorLevel,
    mode: 'shared',
    ...nextStock,
  });
}, [
  activeMerchantKey,
  buildOneMerchantStock,
  floorLevel,
  perMerchantStockEnabled,
  tarotNoMerchant,
]);

const activeMerchantStock = React.useMemo(() => {
  if (!merchantStock) return null;

  if (merchantStock.mode === 'per-merchant') {
    if (!activeMerchantKey) return null;
    return merchantStock.byMerchantKey?.[activeMerchantKey] || null;
  }

  return merchantStock;
}, [activeMerchantKey, merchantStock]);

React.useEffect(() => {
  if (!isMerchantOpen) return;
  if (!combatLibrary || combatLibrary.length === 0) return;

  if (perMerchantStockEnabled) {
    if (!activeMerchantKey) return;

    const hasActiveMerchantStock =
      merchantStock?.floor === floorLevel &&
      merchantStock?.mode === 'per-merchant' &&
      !!merchantStock?.byMerchantKey?.[activeMerchantKey];

    if (hasActiveMerchantStock) return;

    buildMerchantStock(activeMerchantKey);
    return;
  }

  if (merchantStock?.floor === floorLevel && merchantStock?.mode !== 'per-merchant') {
    return;
  }

  buildMerchantStock(null);
}, [
  activeMerchantKey,
  buildMerchantStock,
  combatLibrary,
  floorLevel,
  isMerchantOpen,
  merchantStock,
  perMerchantStockEnabled,
]);

const updateDisplayedMerchantStock = React.useCallback((updater) => {
  setMerchantStock((prev) => {
    if (!prev) return prev;

    if (prev.mode === 'per-merchant') {
      if (!activeMerchantKey) return prev;

      const current = prev.byMerchantKey?.[activeMerchantKey];
      if (!current) return prev;

      return {
        ...prev,
        byMerchantKey: {
          ...(prev.byMerchantKey || {}),
          [activeMerchantKey]: updater(current),
        },
      };
    }

    return updater(prev);
  });
}, [activeMerchantKey]);

  const onBuyCard = React.useCallback((cardId) => {
    const price = activeMerchantStock?.cardPrice ?? getMerchantPrices().cardPrice;

    if (!cardId) return;
    if (teeth < price) return;

    setTeeth((t) => t - price);
    setPlayerDeckIds((prev) => [...prev, cardId]);

updateDisplayedMerchantStock((s) => ({
  ...s,
  cards: (s.cards || []).filter((c) => c.id !== cardId),
}));
  }, [getMerchantPrices, merchantStock, setPlayerDeckIds, setTeeth, teeth]);

  const onBuyTrinket = React.useCallback((relicId) => {
    const price = activeMerchantStock?.trinketPrice ?? getMerchantPrices().trinketPrice;

    if (!relicId) return;
    if (teeth < price) return;
    if ((ownedRelics || []).length >= TRINKET_LIMIT) return;

    const relic = relicsPool.find((r) => r.id === relicId);
    if (!relic) return;
    if ((ownedRelics || []).some((r) => r.id === relicId)) return;

    const ctx = {
      addPotions: (n) =>
        setPotions((p) => Math.min(maxPotions, p + (Number(n) || 0))),
      addSanity: (n) =>
        setSanity((s) => s + (Number(n) || 0)),
      addKeys: (n) =>
        setKeys((k) => Math.min(maxKeys, k + (Number(n) || 0))),
      addTorches: (n) =>
        setTorches((t) => Math.min(maxTorches, t + (Number(n) || 0))),
      addTeeth: (n) =>
        setTeeth((t) => Math.min(maxTeeth, t + (Number(n) || 0))),
      setNextCombatCost0: (n) =>
        setNextCombatCost0((v) => Math.max(v, Number(n) || 0)),
    };

    relic.effect?.(ctx);

    setTeeth((t) => t - price);
    setOwnedRelics((prev) => [...prev, relic]);

updateDisplayedMerchantStock((s) => ({
  ...s,
  trinkets: (s.trinkets || []).filter((r) => r.id !== relicId),
}));
  }, [
    TRINKET_LIMIT,
    activeMerchantStock,
    updateDisplayedMerchantStock,
    getMerchantPrices,
    maxKeys,
    maxPotions,
    maxTeeth,
    maxTorches,
    ownedRelics,
    setPlayerDeckIds,
    setKeys,
    setNextCombatCost0,
    setOwnedRelics,
    setPotions,
    setSanity,
    setTeeth,
    setTorches,
    teeth,
    updateDisplayedMerchantStock,
  ]);

  const onBuyTorch = React.useCallback(() => {
    if (teeth > 0 && torches < maxTorches) {
      setTorches((t) => Math.min(maxTorches, t + 1));
      setTeeth((t) => t - 1);
    }
  }, [maxTorches, setTeeth, setTorches, teeth, torches]);

  const onBuyKey = React.useCallback(() => {
    if (teeth > 0 && keys < maxKeys) {
      setKeys((k) => Math.min(maxKeys, k + 1));
      setTeeth((t) => t - 1);
    }
  }, [keys, maxKeys, setKeys, setTeeth, teeth]);

  const onBuyPotion = React.useCallback(() => {
    if (teeth > 0 && potions < maxPotions) {
      setPotions((p) => Math.min(maxPotions, p + 1));
      setTeeth((t) => t - 1);
    }
  }, [maxPotions, potions, setPotions, setTeeth, teeth]);

return {
  isMerchantOpen,
  setIsMerchantOpen,
  merchantStock,
  setMerchantStock,
  activeMerchantStock,
  activeMerchantKey,
  setActiveMerchantKey,
  getMerchantPrices,
  onBuyCard,
  onBuyTrinket,
  onBuyTorch,
  onBuyKey,
  onBuyPotion,
};
}